/**
 * Statement file → grid of cells, on this device. The file is read with `File.arrayBuffer()` and never uploaded.
 * CSV is parsed here; Excel (.xlsx/.xls) goes through SheetJS, loaded only when an Excel file is picked.
 */
import { dateFromSerial, type Cell, type Grid } from "./statement";

export const MAX_STATEMENT_BYTES = 10 * 1024 * 1024;

export class StatementReadError extends Error {}

/** RFC-4180-ish CSV: quotes, doubled quotes, CRLF, BOM, and a comma / semicolon / tab / pipe delimiter chosen from the first lines. */
export function parseCsv(input: string): Grid {
  const text = input.replace(/^﻿/, "");
  const sample = text.split(/\r?\n/).filter((l) => l.trim() !== "").slice(0, 10).join("\n");
  const count = (ch: string) => sample.split(ch).length - 1;
  const delimiter = ([",", ";", "\t", "|"] as const).reduce((best, ch) => (count(ch) > count(best) ? ch : best), ",");
  const rows: Grid = [];
  let row: Cell[] = [];
  let field = "";
  let quoted = false;
  let wasQuoted = false;
  const endField = () => {
    row.push(wasQuoted || field.trim() !== "" ? field.trim() : null);
    field = "";
    wasQuoted = false;
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field.trim() === "") {
      quoted = true;
      wasQuoted = true;
      field = "";
    } else if (ch === delimiter) endField();
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      endField();
      rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== "" || row.length > 0 || wasQuoted) {
    endField();
    rows.push(row);
  }
  return rows;
}

export type StatementKind = "csv" | "excel" | "pdf" | "unknown";

export function kindOf(name: string, type = ""): StatementKind {
  const n = name.toLowerCase();
  if (n.endsWith(".pdf") || type === "application/pdf") return "pdf";
  if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".xlsm")) return "excel";
  if (n.endsWith(".csv") || n.endsWith(".txt") || n.endsWith(".tsv") || type === "text/csv") return "csv";
  return "unknown";
}

/** An Excel sheet as a grid. Date-formatted numbers become YYYY-MM-DD; other numbers stay numbers (read exactly by `parseAmount`). */
export async function readExcel(buffer: ArrayBuffer): Promise<Grid> {
  const XLSX = await import("xlsx");
  let wb;
  try {
    wb = XLSX.read(new Uint8Array(buffer), { type: "array", cellDates: false, cellNF: true });
  } catch {
    throw new StatementReadError("This file could not be opened as an Excel workbook.");
  }
  // The sheet with the most cells: statements sometimes carry a cover sheet first.
  let best: { rows: Grid; size: number } | null = null;
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws || !ws["!ref"]) continue;
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const rows: Grid = [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      const row: Cell[] = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (!cell || cell.v === undefined || cell.v === null) row.push(null);
        else if (cell.t === "n") row.push(cell.z && XLSX.SSF.is_date(String(cell.z)) ? dateFromSerial(cell.v as number) : (cell.v as number));
        else if (cell.t === "s" || cell.t === "str") row.push(String(cell.v).trim() || null);
        else if (cell.t === "b") row.push(cell.v ? "TRUE" : "FALSE");
        else row.push(null);
      }
      rows.push(row);
    }
    const size = rows.reduce((n, row) => n + row.filter((x) => x !== null).length, 0);
    if (!best || size > best.size) best = { rows, size };
  }
  if (!best || best.size === 0) throw new StatementReadError("This workbook has no data.");
  return best.rows;
}

/** Reads the picked file. Throws `StatementReadError` with a message fit for the screen. */
export async function readStatement(file: File): Promise<Grid> {
  const kind = kindOf(file.name, file.type);
  if (kind === "pdf") throw new StatementReadError("PDF statements are not supported yet. In net banking, download the statement as Excel or CSV instead.");
  if (kind === "unknown") throw new StatementReadError("Choose a CSV or Excel (.xlsx, .xls) statement.");
  if (file.size === 0) throw new StatementReadError("This file is empty.");
  if (file.size > MAX_STATEMENT_BYTES) throw new StatementReadError("This file is larger than 10 MB. Download a shorter date range.");
  const buffer = await file.arrayBuffer();
  if (kind === "excel") return readExcel(buffer);
  return parseCsv(new TextDecoder("utf-8").decode(buffer));
}
